import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { SERVER_URL } from "./config";
import "./GiftPage.css";

export default function GiftPage() {
  const { invitation_token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [couple, setCouple] = useState(null);

  const [giverName, setGiverName] = useState("");
  const [amountRaw, setAmountRaw] = useState("");
  const [blessing, setBlessing] = useState("");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);

  const [serverQuote, setServerQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const FEE_PERCENT = 3.5;

  const [showPayment, setShowPayment] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paymentRef, setPaymentRef] = useState(null);
  const [paying, setPaying] = useState(false);
  const poller = useRef(null);

  const toNumber = (v) => {
    if (typeof v === "string") v = v.replace(",", ".");
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatCurrency = (n) => {
    if (n == null || isNaN(n)) return "—";
    return new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 2 }).format(n);
  };

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(e).trim());

  const normalizePhone = (p) => {
    let s = String(p || "").replace(/[^\d+]/g, "");
    if (s.startsWith("+972")) s = "0" + s.slice(4);
    if (s.startsWith("972")) s = "0" + s.slice(3);
    return s;
  };

  const isValidIlMobile = (p) => /^05\d{8}$/.test(normalizePhone(p));

  const appendParentDomain = (url, parent) => {
    if (!url) return url;
    const hasQ = url.includes("?");
    return url + (hasQ ? "&" : "?") + "parent_domain=" + encodeURIComponent(parent || window.location.origin);
  };

  const title = useMemo(() => {
    if (!couple) return "הענקת מתנה";
    const b = couple?.bride_name?.trim?.() || "";
    const g = couple?.groom_name?.trim?.() || "";
    return `הענקת מתנה ל${b}${g ? " & " + g : ""}`;
  }, [couple]);

  const canSubmit = useMemo(() => {
    return (
      giverName.trim().length > 1 &&
      toNumber(amountRaw) > 0 &&
      isValidEmail(email) &&
      isValidIlMobile(phone)
    );
  }, [giverName, amountRaw, email, phone]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${SERVER_URL}gifts/${invitation_token}/`);
        if (!mounted) return;
        if (data?.ok) {
          setCouple(data.couple || null);
        } else {
          setError("לא נמצא אירוע.");
        }
      } catch (e) {
        setError("שגיאה בטעינת הדף.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [invitation_token]);

  const onOpenConfirm = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setQuoteError("");
    setServerQuote(null);
    setQuoteLoading(true);
    try {
      const { data } = await axios.post(`${SERVER_URL}api/gifts/quote/`, {
        amount_raw: toNumber(amountRaw),
      });
      if (data?.ok) {
        setServerQuote(data);
        setShowConfirm(true);
      } else {
        setQuoteError("שגיאה בחישוב הסכום.");
      }
    } catch {
      setQuoteError("שגיאה בתקשורת עם השרת.");
    } finally {
      setQuoteLoading(false);
    }
  };

  useEffect(() => {
    if (!showConfirm) return;
    const onKey = (e) => e.key === "Escape" && setShowConfirm(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showConfirm]);

  const onBackdropClick = () => setShowConfirm(false);
  const onModalClick = (e) => e.stopPropagation();
  const onCloseConfirm = () => setShowConfirm(false);

  const onProceed = async () => {
    if (!serverQuote) return;
    setPaying(true);
    try {
      const totalToCharge = Number(serverQuote.amount_total);
      const { data } = await axios.post(`${SERVER_URL}payments/tranzila/pay_init/`, {
        amount: totalToCharge,
        giver_name: giverName.trim(),
        invitation_token,
        email: email.trim(),
        phone: normalizePhone(phone),
      });
      if (data?.ok && data?.iframe_url) {
        setShowConfirm(false);
        const urlWithParent = appendParentDomain(data.iframe_url, data.parent_domain);
        setPaymentUrl(urlWithParent);
        setPaymentRef(data.payment_ref || null);
        setShowPayment(true);
      } else {
        alert("שגיאה ביצירת תשלום.");
      }
    } catch {
      alert("שגיאה בתקשורת עם השרת (תשלום).");
    } finally {
      setPaying(false);
    }
  };

  useEffect(() => {
    if (!showPayment) return;
    const handler = (e) => {
      if (!e?.data || typeof e.data !== "object") return;
      if (e.data.type !== "TRZ_STATUS") return;
      if (e.data.status === "success") {
        if (poller.current) clearInterval(poller.current);
        let tries = 0;
        poller.current = setInterval(async () => {
          tries += 1;
          try {
            const res = await axios.get(`${SERVER_URL}payments/status`, { params: { ref: paymentRef } });
            if (res.data?.ok && res.data.status === "PAID") {
              clearInterval(poller.current);
              try {
                const payload = {
                  invitation_token,
                  giver_name: giverName.trim(),
                  amount: String(serverQuote?.amount_raw ?? toNumber(amountRaw)),
                  blessing: blessing || "",
                  tranzila_txn_id: res.data.tranzila_txn_id || null,
                  payment_ref: paymentRef,
                };
                const save = await axios.post(`${SERVER_URL}api/gifts/create/`, payload);
                setShowPayment(false);
                if (save.data?.ok) {
                  navigate("/gift/thank-you", {
                    state: {
                      gift_id: save.data.gift_id,
                      amount: Number(serverQuote?.amount_total ?? 0),
                      couple,
                    },
                    replace: true,
                  });
                } else {
                  alert("התשלום הצליח, אך שמירת המתנה נכשלה.");
                }
              } catch {
                setShowPayment(false);
                alert("התשלום הצליח, אך היתה שגיאה בשמירת המתנה.");
              }
            } else if (res.data?.ok && res.data.status === "FAIL") {
              clearInterval(poller.current);
              setShowPayment(false);
              alert("התשלום נכשל.");
            } else if (tries > 20) {
              clearInterval(poller.current);
              setShowPayment(false);
              alert("לא התקבל אישור עסקה בזמן. נסי שוב.");
            }
          } catch {}
        }, 1500);
      } else {
        setShowPayment(false);
        alert(e.data?.error || "התשלום בוטל/נכשל.");
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      if (poller.current) clearInterval(poller.current);
    };
  }, [showPayment, giverName, amountRaw, blessing, paymentRef, serverQuote, couple, invitation_token, navigate]);

  if (loading) return <div className="p-6 text-center">טוען…</div>;
  if (error) return <div className="p-6 text-center text-red-600">{error}</div>;

  const emailInvalid = email.length > 0 && !isValidEmail(email);
  const phoneInvalid = phone.length > 0 && !isValidIlMobile(phone);

  return (
    <>
      <div dir="rtl" className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow p-6">
          <h1 className="text-2xl font-bold mb-2 text-center">{title}</h1>
          {(couple?.hall_name || couple?.event_date) && (
            <p className="text-center text-sm text-gray-600 mb-6">
              {couple?.hall_name ? `אולם: ${couple.hall_name}` : ""}
              {couple?.hall_name && couple?.event_date ? " · " : ""}
              {couple?.event_date ? `תאריך: ${couple.event_date}` : ""}
            </p>
          )}

          <form className="space-y-4" onSubmit={onOpenConfirm}>
            <div>
              <label className="block mb-1 font-medium">שם מלא</label>
              <input
                className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black/20"
                value={giverName}
                onChange={(e) => setGiverName(e.target.value)}
                placeholder="שם מלא"
                required
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">אימייל</label>
              <input
                type="email"
                className={`w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black/20 ${emailInvalid ? "border-red-500" : ""}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
              />
              {emailInvalid && <div className="text-xs text-red-600 mt-1">נא להזין כתובת אימייל תקינה</div>}
            </div>

            <div>
              <label className="block mb-1 font-medium">טלפון נייד</label>
              <input
                inputMode="tel"
                className={`w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black/20 ${phoneInvalid ? "border-red-500" : ""}`}
                value={phone}
                onChange={(e) => setPhone(normalizePhone(e.target.value))}
                placeholder="05XXXXXXXX"
                required
              />
              {phoneInvalid && <div className="text-xs text-red-600 mt-1">נא להזין מספר נייד ישראלי בפורמט 05XXXXXXXX</div>}
            </div>

            <div>
              <label className="block mb-1 font-medium">סכום המתנה (₪)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black/20"
                value={amountRaw}
                onChange={(e) => setAmountRaw(e.target.value)}
                placeholder="סכום המתנה"
                required
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">ברכה (לא חובה)</label>
              <textarea
                className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black/20"
                rows={4}
                value={blessing}
                onChange={(e) => setBlessing(e.target.value)}
                placeholder="כמה מילים לזוג"
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full py-3 rounded-xl font-semibold transition ${
                canSubmit
                  ? "bg-black text-white hover:opacity-90"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
            >
              המשך לתשלום
            </button>
          </form>
        </div>
      </div>

      {showConfirm && (
        <div
          className="modal-overlay"
          onClick={onBackdropClick}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-content"
            onClick={onModalClick}
          >
            <h2 className="modal-title">
              שימו לב, יש עמלת שירות של {FEE_PERCENT}%.
            </h2>
            <div className="modal-amount-box">
              <div className="modal-amount-label">
                סכום המתנה יהיה:
              </div>
              <div className="modal-amount-value">
                {serverQuote
                  ? formatCurrency(Number(serverQuote.amount_total))
                  : quoteLoading
                  ? "טוען…"
                  : "—"}
              </div>
            </div>
            {quoteError && (
              <p className="modal-error">{quoteError}</p>
            )}
            <div className="modal-buttons">
              <button
                onClick={onProceed}
                disabled={!serverQuote || quoteLoading || paying}
                className={`modal-button modal-button-primary ${
                  !serverQuote || quoteLoading || paying ? "modal-button-disabled" : ""
                }`}
              >
                {paying ? "מכין תשלום…" : "המשך לתשלום"}
              </button>
              <button
                onClick={onCloseConfirm}
                className="modal-button modal-button-secondary"
              >
                חזרה
              </button>
            </div>
          </div>
        </div>
      )}

      {showPayment && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowPayment(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">תשלום מאובטח</h2>
            {!paymentUrl ? (
              <div className="p-4 text-center">טוען…</div>
            ) : (
              <iframe
                title="Tranzila Payment"
                src={paymentUrl}
                style={{ border: 0, width: "100%", height: 520 }}
                allow="payment *"
              />
            )}
            <div className="modal-buttons">
              <button className="modal-button modal-button-secondary" onClick={() => setShowPayment(false)}>
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
